import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { api } from '../../services/api';
import { Plus, Trash2, LogOut, Save, Check, Clock, AlertCircle, X, Download } from 'lucide-react';
import { generateLunchMenuPDF } from '../../utils/pdfMenu';

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

const emptyDish = () => ({
  fi: { name: '', description: '' },
  en: { name: '', description: '' }
});

const emptyMenu = () => ({
  monday: { dishes: [] }, tuesday: { dishes: [] }, wednesday: { dishes: [] },
  thursday: { dishes: [] }, friday: { dishes: [] }
});

const pad = (n) => String(n).padStart(2, '0');

const AdminPanel = ({ onLogout }) => {
  const { t } = useLanguage();
  const [activeDay, setActiveDay] = useState('monday');
  const [menuData, setMenuData] = useState(emptyMenu());
  const [weekNumber, setWeekNumber] = useState('');
  const [pending, setPending] = useState(null);
  const [activateAt, setActivateAt] = useState(null);
  const [serverTime, setServerTime] = useState(null);
  const [savingType, setSavingType] = useState(null); // 'instant' | 'schedule' | null
  const [flash, setFlash] = useState(null); // 'saved' | 'scheduled' | null
  const [error, setError] = useState('');
  const [schedHours, setSchedHours] = useState(0);
  const [schedMinutes, setSchedMinutes] = useState(30);
  const [remaining, setRemaining] = useState(0);

  const loadMenu = useCallback(async () => {
    try {
      const data = await api.adminGetMenu();
      // If a pending menu is already scheduled, edit it; otherwise edit current
      const base = data.pending || data.current || emptyMenu();
      // Ensure all days exist
      const normalized = { ...emptyMenu(), ...base };
      DAY_ORDER.forEach(d => { if (!normalized[d]) normalized[d] = { dishes: [] }; });
      setMenuData(normalized);
      // Prefer pending_week while editing a pending menu
      const w = (data.pending_week != null ? data.pending_week : data.current_week) ?? '';
      setWeekNumber(w === null ? '' : String(w));
      setPending(data.pending || null);
      setActivateAt(data.activate_at || null);
      setServerTime(data.server_time || null);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    }
  }, [onLogout]);

  useEffect(() => { loadMenu(); }, [loadMenu]);

  // Countdown for pending timer
  useEffect(() => {
    if (!activateAt || !serverTime) { setRemaining(0); return; }
    const target = new Date(activateAt).getTime();
    const drift = new Date(serverTime).getTime() - Date.now();
    const tick = () => {
      const r = Math.max(0, target - (Date.now() + drift));
      setRemaining(r);
      if (r <= 0) {
        setTimeout(loadMenu, 1500);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [activateAt, serverTime, loadMenu]);

  const dishes = menuData[activeDay]?.dishes || [];

  const updateDish = (idx, lang, field, value) => {
    setMenuData(prev => {
      const next = { ...prev, [activeDay]: { ...prev[activeDay], dishes: [...prev[activeDay].dishes] } };
      next[activeDay].dishes[idx] = {
        ...next[activeDay].dishes[idx],
        [lang]: { ...next[activeDay].dishes[idx][lang], [field]: value }
      };
      return next;
    });
  };

  const addDish = () => {
    setMenuData(prev => ({
      ...prev,
      [activeDay]: { ...prev[activeDay], dishes: [...(prev[activeDay]?.dishes || []), emptyDish()] }
    }));
  };

  const removeDish = (idx) => {
    setMenuData(prev => ({
      ...prev,
      [activeDay]: { ...prev[activeDay], dishes: prev[activeDay].dishes.filter((_, i) => i !== idx) }
    }));
  };

  const handleInstantSave = async () => {
    setSavingType('instant');
    setError('');
    try {
      const w = weekNumber === '' ? null : Number(weekNumber);
      await api.instantUpdate(menuData, w);
      setFlash('saved');
      setTimeout(() => setFlash(null), 2000);
      await loadMenu();
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingType(null);
    }
  };

  const handleSchedule = async () => {
    setSavingType('schedule');
    setError('');
    try {
      const w = weekNumber === '' ? null : Number(weekNumber);
      await api.schedule(menuData, Number(schedHours) || 0, Number(schedMinutes) || 0, w);
      setFlash('scheduled');
      setTimeout(() => setFlash(null), 2000);
      await loadMenu();
    } catch (e) {
      setError(e.response?.data?.detail || 'Schedule failed');
    } finally {
      setSavingType(null);
    }
  };

  const handleCancelSchedule = async () => {
    try {
      await api.cancelSchedule();
      await loadMenu();
    } catch (e) {
      setError('Cancel failed');
    }
  };

  const totalSec = Math.floor(remaining / 1000);
  const rH = Math.floor(totalSec / 3600);
  const rM = Math.floor((totalSec % 3600) / 60);
  const rS = totalSec % 60;

  return (
    <div className="max-w-5xl mx-auto pb-28">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-[#1F2A24] font-semibold">{t.admin.manageLunch}</h1>
          <p className="text-[#6B6257] text-sm mt-1">Ravintola La Maria</p>
        </div>
        <button onClick={onLogout} className="inline-flex items-center gap-2 text-[#6B6257] hover:text-[#7A1F25] text-sm font-medium">
          <LogOut size={16} /> {t.admin.logout}
        </button>
      </div>

      {pending && activateAt && remaining > 0 && (
        <div className="mb-6 bg-gradient-to-r from-[#7A1F25] to-[#9B2730] text-white rounded-xl p-5 flex items-center justify-between gap-4 flex-wrap shadow-md">
          <div className="flex items-center gap-3">
            <Clock className="text-[#D4A94B]" size={22} />
            <div>
              <div className="text-[#D4A94B] text-xs tracking-[0.2em] uppercase font-medium">{t.admin.pendingNotice}</div>
              <div className="font-serif text-2xl md:text-3xl tabular-nums">
                {pad(rH)}:{pad(rM)}:{pad(rS)}
              </div>
            </div>
          </div>
          <button onClick={handleCancelSchedule} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white px-4 py-2 rounded-full text-sm">
            <X size={14} /> {t.admin.cancelSchedule}
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 flex items-center gap-2">
          <AlertCircle size={18} /> {error}
        </div>
      )}

      <div className="bg-white border border-[#EDE4D2] rounded-xl p-5 mb-6 flex items-center gap-4 flex-wrap">
        <label className="font-serif text-lg text-[#1F2A24]">{t.admin.weekNumber}</label>
        <input
          type="number"
          min="1"
          max="53"
          value={weekNumber}
          onChange={(e) => setWeekNumber(e.target.value)}
          className="w-28 px-3 py-2 rounded-lg border border-[#E8DFCF] focus:outline-none focus:ring-2 focus:ring-[#B8934A]/40 focus:border-[#B8934A] bg-[#FAF6F0] text-center tabular-nums text-lg font-semibold"
          placeholder="53"
        />
        <span className="text-sm text-[#6B6257]">{t.lunch.week} {weekNumber || '—'}</span>

        <div className="ml-auto flex items-center gap-2">
          <span className="hidden md:inline text-xs text-[#6B6257] uppercase tracking-wider mr-1">{t.admin.printablePdf}</span>
          <button
            onClick={() => generateLunchMenuPDF({ menu: menuData, week: weekNumber, language: 'fi' })}
            className="inline-flex items-center gap-2 bg-[#7A1F25] hover:bg-[#5A1219] text-white px-4 py-2 rounded-full text-sm font-medium shadow-sm hover:shadow transition-all"
            title="PDF Suomeksi"
          >
            <Download size={14} /> PDF · FI
          </button>
          <button
            onClick={() => generateLunchMenuPDF({ menu: menuData, week: weekNumber, language: 'en' })}
            className="inline-flex items-center gap-2 bg-[#7A1F25] hover:bg-[#5A1219] text-white px-4 py-2 rounded-full text-sm font-medium shadow-sm hover:shadow transition-all"
            title="PDF in English"
          >
            <Download size={14} /> PDF · EN
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {DAY_ORDER.map(day => (
          <button
            key={day}
            onClick={() => setActiveDay(day)}
            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
              activeDay === day
                ? 'bg-[#7A1F25] text-white shadow-md'
                : 'bg-white text-[#1F2A24] border border-[#E8DFCF] hover:bg-[#F0E9DC]'
            }`}
          >
            {t.lunch.days[day]}
            {menuData[day]?.dishes?.length ? (
              <span className={`ml-2 text-xs ${activeDay === day ? 'text-[#D4A94B]' : 'text-[#B8934A]'}`}>
                {menuData[day].dishes.length}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div className="space-y-5">
        {dishes.map((dish, idx) => (
          <div key={idx} className="bg-white border border-[#EDE4D2] rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#F4EDDC] flex items-center justify-center border border-[#E6D9B8]">
                  <span className="font-serif text-[#B8934A] font-semibold">{idx + 1}</span>
                </div>
                <span className="font-serif text-lg text-[#1F2A24]">{t.lunch.days[activeDay]}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => removeDish(idx)}
                  className="text-red-600 hover:text-red-700 p-2 rounded-full hover:bg-red-50 transition-colors"
                  aria-label="remove"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-[#6B6257] uppercase tracking-wider font-medium">{t.admin.dishName}</label>
                  <input
                    type="text"
                    value={dish.fi?.name || ''}
                    onChange={(e) => updateDish(idx, 'fi', 'name', e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8DFCF] focus:outline-none focus:ring-2 focus:ring-[#B8934A]/40 focus:border-[#B8934A] bg-[#FAF6F0]"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#6B6257] uppercase tracking-wider font-medium">{t.admin.dishDesc}</label>
                  <textarea
                    value={dish.fi?.description || ''}
                    onChange={(e) => updateDish(idx, 'fi', 'description', e.target.value)}
                    rows={3}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8DFCF] focus:outline-none focus:ring-2 focus:ring-[#B8934A]/40 focus:border-[#B8934A] bg-[#FAF6F0] resize-none"
                  />
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-[#6B6257] uppercase tracking-wider font-medium">{t.admin.dishNameEn}</label>
                  <input
                    type="text"
                    value={dish.en?.name || ''}
                    onChange={(e) => updateDish(idx, 'en', 'name', e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8DFCF] focus:outline-none focus:ring-2 focus:ring-[#B8934A]/40 focus:border-[#B8934A] bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#6B6257] uppercase tracking-wider font-medium">{t.admin.dishDescEn}</label>
                  <textarea
                    value={dish.en?.description || ''}
                    onChange={(e) => updateDish(idx, 'en', 'description', e.target.value)}
                    rows={3}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E8DFCF] focus:outline-none focus:ring-2 focus:ring-[#B8934A]/40 focus:border-[#B8934A] bg-white resize-none"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}

        <button
          onClick={addDish}
          className="w-full border-2 border-dashed border-[#D6C9AE] hover:border-[#B8934A] hover:bg-white rounded-xl py-5 flex items-center justify-center gap-2 text-[#6B6257] hover:text-[#B8934A] transition-all font-medium"
        >
          <Plus size={18} /> {t.admin.add}
        </button>
      </div>

      {/* Schedule & Save footer */}
      <div className="mt-10 bg-white border border-[#EDE4D2] rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="text-[#B8934A]" size={18} />
          <h3 className="font-serif text-xl text-[#1F2A24] font-semibold">{t.admin.scheduleTitle}</h3>
        </div>
        <p className="text-sm text-[#6B6257] mb-5">{t.admin.scheduleHint}</p>

        <div className="grid md:grid-cols-[auto_auto_1fr] gap-4 items-end">
          <div>
            <label className="text-xs text-[#6B6257] uppercase tracking-wider font-medium">{t.admin.hours}</label>
            <input
              type="number" min="0" max="72" value={schedHours}
              onChange={(e) => setSchedHours(e.target.value)}
              className="mt-1 w-24 px-3 py-2 rounded-lg border border-[#E8DFCF] focus:outline-none focus:ring-2 focus:ring-[#B8934A]/40 focus:border-[#B8934A] bg-[#FAF6F0] text-center tabular-nums"
            />
          </div>
          <div>
            <label className="text-xs text-[#6B6257] uppercase tracking-wider font-medium">{t.admin.minutes}</label>
            <input
              type="number" min="0" max="59" value={schedMinutes}
              onChange={(e) => setSchedMinutes(e.target.value)}
              className="mt-1 w-24 px-3 py-2 rounded-lg border border-[#E8DFCF] focus:outline-none focus:ring-2 focus:ring-[#B8934A]/40 focus:border-[#B8934A] bg-[#FAF6F0] text-center tabular-nums"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
            <button
              onClick={handleInstantSave}
              disabled={savingType !== null}
              className="inline-flex items-center justify-center gap-2 border border-[#7A1F25] text-[#7A1F25] hover:bg-[#7A1F25] hover:text-white disabled:opacity-60 px-6 py-3 rounded-full font-medium transition-all"
            >
              {flash === 'saved' ? <><Check size={16} /> {t.admin.saved}</> : <><Save size={16} /> {t.admin.saveInstant}</>}
            </button>
            <button
              onClick={handleSchedule}
              disabled={savingType !== null}
              className="inline-flex items-center justify-center gap-2 bg-[#B8934A] hover:bg-[#A7823C] disabled:opacity-60 text-white px-6 py-3 rounded-full font-medium shadow-lg hover:shadow-xl transition-all"
            >
              {flash === 'scheduled' ? <><Check size={16} /> {t.admin.scheduled}</> : <><Clock size={16} /> {t.admin.scheduleBtn}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
